import { useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { brainsEndpoints as endpoints } from '@/api/endpoints';
import { callGet, callPost } from '@/api/fetcher';
import { Brain } from '@/shared/types/brain';
import { EventName } from '@/shared/types/segment';

import { brainCache } from './useBrains';
type S<T, K extends keyof T, R extends keyof T> = Pick<T, K> & Partial<Pick<T, R>>;

// brain_id is required, other are optional
type TemplateAsync = S<
  Brain,
  'brain_id',
  'name' | 'description' | 'language' | 'confidence_threshold' | 'guidelines' | 'brain_type'
>;

type Template = {
  id: string;
  category: string;
  integration_id: string;
  advanced?: boolean;
  languages: Record<string, { brain_id: string }>;
};

type Metadata = { categories: string[]; templates: Template[] };

export const API = Object.freeze({
  getMetadata: async (): Promise<Metadata> => callGet(`/templates/metadata.json`),
  createBrain: async ({ brain_id, ...rest }: TemplateAsync): Promise<Brain> =>
    callPost(`/www/api/templates/${brain_id}`, rest),
  getTemplate: async (lang: string): Promise<Brain> => callGet(endpoints.templates(lang)),
});

/**
 * Brain templates
 */
export const useTemplates = (templateId?: string) => {
  const queryClient = useQueryClient();

  const { data: metadata, status: metadataStatus } = useQuery<Metadata, Error>({
    queryKey: ['templates'],
    queryFn: API.getMetadata,
  });

  const templateBrain = useCallback(
    async (language: string) => await API.getTemplate(language),
    []
  );

  const {
    mutateAsync: createBrainFromTemplateMutationAsync,
    status: createBrainFromTemplateStatus,
  } = useMutation<Brain, Error, TemplateAsync>({
    mutationFn: API.createBrain,
    meta: {
      errorToast: true,
      segment: {
        success: EventName.AIAgentCreated,
        action: 'ai_agent_create',
        props: { source: 'template' },
        successProps: (brain: Brain) => ({ brain_id: brain.brain_id }),
      },
    },
    onSuccess: (resp) => {
      brainCache.created(queryClient, resp);
    },
  });

  const createBrainFromTemplateAsync = useCallback(
    (
      data: TemplateAsync,
      options?: {
        onSuccess?: (resp: Brain) => void;
        onError?: (error: Error) => void;
        onSettled?: () => void;
      }
    ) =>
      createBrainFromTemplateMutationAsync(data, {
        onSuccess: options?.onSuccess,
        onError: options?.onError,
        onSettled: options?.onSettled,
      }),
    [createBrainFromTemplateMutationAsync]
  );

  const template = metadata?.templates?.find((t) => t.id === templateId);

  return {
    templateBrain,
    metadata,
    template,
    metadataStatus,
    createBrainFromTemplateAsync,
    createBrainFromTemplateStatus,
  };
};
